/**
 * Date Range Utilities for API Queries
 *
 * This module provides utilities for converting UI date selections into API-compatible
 * date range parameters using the half-open interval pattern [start, end).
 */

/**
 * Adds one day to a date, properly handling DST transitions.
 * Using setDate() instead of millisecond arithmetic ensures correct behavior
 * during DST transitions (when days are 23 or 25 hours).
 */
const addOneDay = (date: Date): Date => {
  const d = new Date(date);
  d.setDate(d.getDate() + 1);
  return d;
};

/**
 * Get user's timezone abbreviation (e.g., "EST", "PST", "UTC").
 * Uses the browser's Intl API to get the localized timezone name.
 *
 * @returns Timezone abbreviation string
 *
 * @example
 * ```typescript
 * const tz = getTimezoneAbbreviation(); // "EST" (if user is in Eastern timezone)
 * ```
 */
export function getTimezoneAbbreviation(): string {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'short',
  });
  const parts = formatter.formatToParts(new Date());
  const timeZonePart = parts.find((part) => part.type === 'timeZoneName');
  return timeZonePart?.value || 'UTC';
}

/**
 * Converts UI date range to API format using half-open interval [start, end).
 *
 * Date Range Semantics:
 * - startDate is INCLUSIVE (>= comparison): start of day 00:00:00 in user's local timezone
 * - endDate is EXCLUSIVE (< comparison): start of next day 00:00:00 in user's local timezone
 *
 * This follows the standard half-open interval pattern used in most programming
 * languages and databases, making queries clear and unambiguous:
 * `WHERE timestamp >= startDate AND timestamp < endDate`
 *
 * Timezone Handling:
 * - Uses LOCAL timezone for date boundaries (not UTC) to match user's calendar day
 * - Converts to ISO 8601 string which includes timezone offset for server
 * - Server stores as UTC internally (timestamptz) but receives correct local moment
 * - This ensures "Nov 3" means Nov 3 in user's timezone, not UTC
 *
 * @example Single day selection (user in PST timezone, UTC-8)
 * ```typescript
 * const user_selects = new Date('2025-11-03'); // Nov 3, 2025
 * const result = prepareDateRangeForQuery(user_selects, user_selects);
 * // Returns:
 * // {
 * //   startDate: "2025-11-03T08:00:00.000Z",  // Nov 3 00:00 PST in UTC
 * //   endDate: "2025-11-04T08:00:00.000Z"     // Nov 4 00:00 PST in UTC
 * // }
 * // Query will match all timestamps on Nov 3 in PST timezone
 * ```
 *
 * @example Multi-day range
 * ```typescript
 * const from = new Date('2025-11-01');
 * const to = new Date('2025-11-07');
 * const result = prepareDateRangeForQuery(from, to);
 * // Returns ISO strings representing start of Nov 1 and start of Nov 8
 * // in user's local timezone, ensuring full 7-day range is captured
 * ```
 *
 * @example Usage with tRPC
 * ```typescript
 * const { startDate, endDate } = prepareDateRangeForQuery(
 *   dateRange.from,
 *   dateRange.to
 * );
 * const { data } = trpc.calls.getSummary.useQuery({ startDate, endDate });
 * ```
 *
 * @param from - Start date (inclusive). If undefined, no start date filter.
 * @param to - End date (inclusive). If undefined, no end date filter.
 * @returns Object with optional ISO string dates (undefined if inputs are undefined)
 */
export const prepareDateRangeForQuery = (
  from: Date | undefined,
  to: Date | undefined
): { startDate?: string; endDate?: string } => {
  /**
   * Helper to get start of day in LOCAL timezone (00:00:00.000).
   * Uses local time to ensure date boundaries match user's calendar day.
   * The resulting ISO string includes timezone offset for server storage.
   */
  const startOfDayLocal = (date: Date): Date => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  // Always return the expected shape for type consistency
  return {
    startDate: from ? startOfDayLocal(from).toISOString() : undefined,
    // Add 1 day to 'to' date to make it exclusive
    // User selects Nov 3, we query for < Nov 4 (which includes all of Nov 3)
    // Using addOneDay() instead of millisecond arithmetic to handle DST correctly
    endDate: to ? startOfDayLocal(addOneDay(to)).toISOString() : undefined,
  };
};
